/**
 * FAMILY SUPPORT REGISTRY — Aggregator (Post-Refactor)
 *
 * This file aggregates support content from all module files into a single
 * FAMILY_SUPPORT_REGISTRY object for backwards compatibility.
 *
 * REFACTORED: 2026-03-25 (Step 5-6)
 * Previous: 4971 lines monolithic registry
 * Current: ~110 lines aggregator pattern
 *
 * Module files location: src/support/modules/
 * - get.support.ts (50 families - 100%)
 * - taa.support.ts (35 families - 100%)
 * - met.support.ts (40 entries: 39 taxonomy + 1 orphan MET-TIJD-DUUR)
 * - sww.support.ts (24 families - 100%)
 * - topo.support.ts (23 families - 100%)
 * - stu.support.ts (20 families - 100%)
 * - spr.support.ts (20 families - 100%)
 * - rek.support.ts (17 families - 100% ✅ COMPLETE)
 * - tijd.support.ts (16 families - 100%)
 * - geld.support.ts (15 families - 100%)
 *
 * Total: 260 entries (259 taxonomy + 1 orphan)
 * Taxonomy: 259 families
 * Coverage: 259/259 = 100% ✅
 * Orphan: 1 (MET-TIJD-DUUR - documented, kept for backwards compatibility)
 *
 * S-6 (Support RC audit 2026): removed dead `validateHintCoverage()` and
 * `logHintCoverage()` functions plus the `HintCoverageResult` interface.
 * Neither was exported from the support barrel nor called from any external
 * consumer. The canonical (and richer) coverage gate lives in
 * `supportCoverageReport.ts` as `generateSupportCoverageReport()` +
 * `assertSupportCoverage()`, which is run from `App.tsx` on boot.
 */

import { HintStep } from './hints';
import { type VisualAid } from '../visuals';

// Import all module support registries
import { STU_SUPPORT, TOPO_SUPPORT, REK_SUPPORT, SPR_SUPPORT, SWW_SUPPORT, TAA_SUPPORT, MET_SUPPORT, TIJD_SUPPORT, GELD_SUPPORT, GET_SUPPORT } from './modules';

/**
 * Type Definitions
 * (Preserved from original registry; live here rather than in types.ts so the
 * module-level *.support.ts files have a single import target for the entry
 * shape. Type-consolidation is tracked as S-4 in the RC audit but deferred.)
 */
export interface RepresentationSupport {
  hints?: HintStep[];
  visual?: VisualAid;
  explanation?: string;
}
export interface FamilySupportEntry {
  familyId: string;
  hints?: HintStep[];
  defaultVisual?: VisualAid;
  defaultExplanation?: string;
  /**
   * Short, conversational coaching line spoken by Djay in the floating
   * speech bubble above the Spiekbriefje drawer. Keep it ≤120 chars and
   * 1 sentence — it is the *affective opener*, not the didactic content
   * (which lives in `hints[]`). When omitted, the drawer derives a line
   * from `defaultExplanation` or falls back to a generic coaching nudge.
   *
   * Good: "Even spieken bij driehoeken? Kijk goed naar de zijden én de hoeken."
   * Bad:  Long taxonomy dumps that duplicate the KERN hint card below.
   */
  bubbleHint?: string;
  representations?: Record<string, RepresentationSupport>;
  /** Whether this family conceptually requires a visual aid */
  requiresVisual?: boolean;
  /** Whether this family conceptually requires hints */
  requiresHints?: boolean;
}

/**
 * FAMILY SUPPORT REGISTRY — Aggregated from Module Files
 *
 * Merges all module support registries into a single object.
 * This maintains backwards compatibility with existing code that imports
 * FAMILY_SUPPORT_REGISTRY directly.
 *
 * Module breakdown (sorted by size):
 * - GET: 50 families (Getalbegrip - largest, 100%)
 * - TAA: 35 families (Taalbegrip - 100%)
 * - MET: 40 entries (39 taxonomy + 1 orphan: MET-TIJD-DUUR)
 * - SWW: 24 families (Spelling Werkwoorden - 100%)
 * - TOPO: 23 families (Topografie - 100%)
 * - STU: 20 families (Studievaardigheden - 100%)
 * - SPR: 20 families (Spelling - 100%)
 * - REK: 17 families (Basisrekenen - 100% ✅)
 * - TIJD: 16 families (Tijd - 100%)
 * - GELD: 15 families (Geld - 100%)
 *
 * Total: 260 entries
 * Taxonomy families: 259
 * Coverage: 259/259 (100% ✅)
 * Orphan: 1 (MET-TIJD-DUUR - not in taxonomy, kept for backwards compatibility)
 */
export const FAMILY_SUPPORT_REGISTRY: Record<string, FamilySupportEntry> = {
  ...STU_SUPPORT,
  ...TOPO_SUPPORT,
  ...REK_SUPPORT,
  ...SPR_SUPPORT,
  ...SWW_SUPPORT,
  ...TAA_SUPPORT,
  ...MET_SUPPORT,
  ...TIJD_SUPPORT,
  ...GELD_SUPPORT,
  ...GET_SUPPORT
};

/**
 * Helper Functions
 *
 * Used by supportResolver.ts and the practice/admin consumers via the
 * support barrel (`src/support/index.ts`).
 */

/**
 * Get support entry for a family
 * @param familyId - The family ID to look up
 * @returns FamilySupportEntry or undefined if not found
 */
export function getFamilySupportEntry(familyId: string): FamilySupportEntry | undefined {
  return FAMILY_SUPPORT_REGISTRY[familyId];
}

/**
 * Get hints for a family
 * @param familyId - The family ID to look up
 * @returns Array of hint steps, or empty array if not found
 */
export function getHintsForFamilyId(familyId: string): HintStep[] {
  const entry = FAMILY_SUPPORT_REGISTRY[familyId];
  return entry?.hints || [];
}

/**
 * Get all supported family IDs
 * @returns Array of all family IDs in the registry
 */
export function getAllSupportedFamilyIds(): string[] {
  return Object.keys(FAMILY_SUPPORT_REGISTRY);
}